/**
 * modals.tsx
 * Three creation modals: NewClient, NewRequest, NewSignature.
 * Each is a Dialog sheet with a clean form. Import and render wherever needed —
 * they are controlled (open/onClose) so they work from any page or quick-action.
 *
 * Uses tRPC mutations for create flows across clients, requests, and signatures.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { getSession } from "@/lib/session";
import { trpc } from "@/lib/trpc";
import { toast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { runtimeConfig } from "@/lib/runtime-config";
import QuickBooksLogo from "@/components/svgs/quickbook.svg";
import XeroLogo from "@/components/svgs/xero.svg";
import {
  canUsePaidMessagingFromSession,
  PAID_MESSAGING_UPGRADE_COPY,
} from "@/lib/paid-messaging";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ACTION_BTN, GHOST_BTN } from "@/lib/ui-tokens";
import { TemplateInput } from "@/components/ui/template-input";
import {
  PHONE_COUNTRY_OPTIONS,
  guessPhoneCountryFromLocale,
  isValidStoredPhoneNumber,
  normalizePhoneNumber,
  parseStoredPhoneNumber,
} from "@quire/types";

// ─── Shared primitives ────────────────────────────────────────────────────────

function Label({
  children,
  htmlFor,
  required,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-semibold text-foreground mb-1"
    >
      {children}
      {required && <span className="ml-0.5 text-(--warn)">*</span>}
    </label>
  );
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-1 text-[10px] text-(--warn)">{msg}</p>;
}

function FormDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 my-1">
      <div className="flex-1 h-px bg-(--border)" />
      <span className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
      <div className="flex-1 h-px bg-(--border)" />
    </div>
  );
}

function ModalFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel = "Create",
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  submitLabel?: string;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-4 border-t border-(--border) mt-4">
      <button type="button" onClick={onClose} className={GHOST_BTN}>
        Cancel
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className={`${ACTION_BTN} disabled:opacity-40`}
      >
        {submitting ? (
          <svg
            className="animate-spin"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <circle
              cx="6"
              cy="6"
              r="4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="14"
              strokeDashoffset="5"
            />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M5 1v8M1 5h8"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="square"
            />
          </svg>
        )}
        {submitting ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

// ─── NewClientModal ───────────────────────────────────────────────────────────

interface NewClientForm {
  name: string;
  email: string;
  phone: string;
  phoneCountry: string;
  whatsappPhone: string;
  whatsappPhoneCountry: string;
  type: "individual" | "business";
  notes: string;
  assignedMemberId: string;
  status?: "active" | "archived";
}

const DEFAULT_PHONE_COUNTRY =
  typeof window !== "undefined"
    ? guessPhoneCountryFromLocale(window.navigator.language)
    : "";

const EMPTY_CLIENT: NewClientForm = {
  name: "",
  email: "",
  phone: "",
  phoneCountry: DEFAULT_PHONE_COUNTRY,
  whatsappPhone: "",
  whatsappPhoneCountry: DEFAULT_PHONE_COUNTRY,
  type: "individual",
  notes: "",
  assignedMemberId: "",
};

function PhoneField({
  idPrefix,
  label,
  phoneValue,
  countryValue,
  onPhoneChange,
  onCountryChange,
  error,
  placeholder,
}: {
  idPrefix: string;
  label: string;
  phoneValue: string;
  countryValue: string;
  onPhoneChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  error?: string;
  placeholder: string;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={`${idPrefix}-phone`}>{label}</Label>
      <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-2">
        <select
          id={`${idPrefix}-country`}
          value={countryValue}
          onChange={(e) => onCountryChange(e.target.value)}
          className="h-9 border border-(--border) bg-card px-2 text-[11px] text-foreground focus:outline-none focus:border-(--brand) transition-colors"
        >
          <option value="">Country</option>
          {PHONE_COUNTRY_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
        <Input
          id={`${idPrefix}-phone`}
          type="tel"
          placeholder={placeholder}
          className="h-9 text-[12px]"
          value={phoneValue}
          onChange={(e) => onPhoneChange(e.target.value)}
        />
      </div>
      <p className="text-[10px] text-muted-foreground">
        Normalized as international number for reliable SMS and WhatsApp
        delivery.
      </p>
      <FieldError msg={error} />
    </div>
  );
}

function derivePhoneCountry(value?: string | null) {
  return parseStoredPhoneNumber(value)?.country || DEFAULT_PHONE_COUNTRY;
}

export function NewClientModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const navigate = useNavigate();
  const currentUserId = session?.userId ?? "";

  const [form, setForm] = useState<NewClientForm>(() => ({
    ...EMPTY_CLIENT,
    assignedMemberId: currentUserId,
  }));
  const [errors, setErrors] = useState<Partial<NewClientForm>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    { row: number; name: string; errors: string[] }[]
  >([]);
  const [showQboImport, setShowQboImport] = useState(false);
  const [showXeroImport, setShowXeroImport] = useState(false);

  const integrations_q = trpc.integrations.list.useQuery(undefined, {
    enabled: open,
  });

  const qboConnected = Boolean(integrations_q.data?.qbo?.accountId);
  const xeroConnected = Boolean(integrations_q.data?.xero?.tenantId);

  const clientCountQuery = trpc.clients.count.useQuery();
  const utils = trpc.useUtils();

  const currentClientCount = clientCountQuery.data ?? 0;

  const plan = session?.plan ?? "trial";
  const role = session?.role ?? "member";
  const canManageClients = role === "admin" || role === "owner";

  const PLAN_LIMITS = { trial: 15, starter: 25, pro: 1000000 };
  const limit = PLAN_LIMITS[plan as keyof typeof PLAN_LIMITS] ?? 5;

  const auditLogMutation = trpc.audit.log.useMutation();

  // Audit log helper
  const logAuditTrail = (
    action: string,
    detail: string,
    entityId: string = "system",
  ) => {
    auditLogMutation.mutate({
      action,
      entityType: "client",
      entityId,
      metadata: { detail },
    });
  };

  const createClient = trpc.clients.create.useMutation({
    onMutate() {
      toast.loading("Adding client...", { id: "client-create" });
    },
    onSuccess(data) {
      logAuditTrail(
        "CREATE_CLIENT",
        `Created client ${data.id} (${form.name})`,
        data.id,
      );
      toast.success("Client added", {
        id: "client-create",
        description: "Taking you to the client's dashboard...",
      });
      onClose();
      setForm({ ...EMPTY_CLIENT, assignedMemberId: currentUserId });
      setAdvancedOpen(false);
      utils.clients.list.invalidate();
      utils.clients.count.invalidate();
      navigate({ to: "/clients/$clientId", params: { clientId: data.id } });
    },
    onError(err) {
      toast.error(err.message || "Failed to create client", {
        id: "client-create",
      });
    },
    onSettled() {
      setSaving(false);
    },
  });

  const bulkCreate = trpc.clients.bulkCreate.useMutation({
    onMutate() {
      toast.loading("Importing clients...", { id: "client-bulk" });
    },
    onSuccess(data) {
      logAuditTrail(
        "BULK_CREATE_CLIENTS",
        `Imported ${data.count} clients from CSV`,
      );
      toast.success("Clients imported", {
        id: "client-bulk",
        description: `${data.count} clients successfully added.`,
      });
      utils.clients.list.invalidate();
      utils.clients.count.invalidate();
      onClose();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to import clients", {
        id: "client-bulk",
      });
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  const triggerDownloadTemplate = () => {
    if (!canManageClients) {
      toast.error("Access denied", {
        description: "Only admins or owners can download templates.",
      });
      return;
    }

    const csv = `name,email,phone,phone_country,whatsapp_phone,whatsapp_phone_country,type(individual/business),notes
Sarah Johnson,sarah.johnson@example.com,+14155550182,US,+14155550182,US,individual,Prefers email communication
Michael Chen,michael.chen@example.com,+447911123456,GB,+447911123456,GB,individual,Quarterly tax client
Greenfield Construction Ltd,accounts@greenfieldconstruction.com,+16155557842,US,+16155557842,US,business,Monthly bookkeeping and payroll
Adewale & Co Advisory,info@adewaleco.com,+2348034567890,NG,+2348034567890,NG,business,Annual compliance engagement
Emma Rodriguez,emma.rodriguez@example.com,+34612345678,ES,+34612345678,ES,individual,Responds faster on WhatsApp
`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "quire_clients_template.csv");

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    logAuditTrail("DOWNLOAD_TEMPLATE", "Downloaded CSV template");
  };

  const downloadTemplate = () => {
    if (!canManageClients) {
      toast.error("Access denied", {
        description: "Only admins or owners can download templates.",
      });
      return;
    }
    setShowDownloadConfirm(true);
  };

  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!canManageClients) {
      toast.error("Access denied", {
        description: "Only admins or owners can import clients.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async ({ target }) => {
      const csv = target?.result as string;
      if (!csv) return;

      const lines = csv.split("\n").filter((l) => l.trim());
      const firstLine = lines[0];
      if (!firstLine) return;

      const headers = parseCsvLine(firstLine).map((h) =>
        h.toLowerCase().replace(/\s+/g, "_"),
      );
      const normalizedHeaders = headers.map((h) => {
        if (h.includes("type")) return "type";
        if (h.includes("whatsapp_phone_country")) return "whatsappPhoneCountry";
        if (h.includes("whatsapp_phone")) return "whatsappPhone";
        if (h.includes("phone_country")) return "phoneCountry";
        if (h.includes("phone")) return "phone";
        return h;
      });

      const parsedRows: any[] = [];
      const errorsList: { row: number; name: string; errors: string[] }[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line || !line.trim()) continue;

        const values = parseCsvLine(line);
        const obj: any = {};
        normalizedHeaders.forEach((header, index) => {
          obj[header] = values[index] || "";
        });

        const rowNum = i + 1;
        const rowErrors: string[] = [];

        const name = obj.name?.trim() || "";
        const email = obj.email?.trim() || "";
        const type = obj.type?.trim().toLowerCase() || "individual";

        if (!name) {
          rowErrors.push("Name is required.");
        } else if (name.length < 2) {
          rowErrors.push("Name must be at least 2 characters.");
        } else if (name.length > 120) {
          rowErrors.push("Name must be under 120 characters.");
        }

        if (!email) {
          rowErrors.push("Email is required.");
        } else if (!/^\S+@\S+\.\S+$/.test(email)) {
          rowErrors.push("Email format is invalid.");
        }

        if (type !== "individual" && type !== "business") {
          rowErrors.push("Type must be either 'individual' or 'business'.");
        }

        if (obj.phone) {
          const phoneResult = normalizePhoneNumber(
            obj.phone,
            (obj.phoneCountry || "") as any,
          );
          if (!phoneResult.ok) {
            rowErrors.push(`Phone error: ${phoneResult.error}`);
          } else {
            obj.phone = phoneResult.e164;
          }
        }

        if (obj.whatsappPhone) {
          const whatsappResult = normalizePhoneNumber(
            obj.whatsappPhone,
            (obj.whatsappPhoneCountry || "") as any,
          );
          if (!whatsappResult.ok) {
            rowErrors.push(`WhatsApp error: ${whatsappResult.error}`);
          } else {
            obj.whatsappPhone = whatsappResult.e164;
          }
        }

        if (rowErrors.length > 0) {
          errorsList.push({
            row: rowNum,
            name: name || `Row ${rowNum}`,
            errors: rowErrors,
          });
        } else {
          obj.type = type;
          obj.assignedMemberId = currentUserId;
          parsedRows.push(obj);
        }
      }

      if (errorsList.length > 0) {
        setValidationErrors(errorsList);
        setShowValidationErrors(true);
        if (csvInputRef.current) csvInputRef.current.value = "";
        return;
      }

      if (currentClientCount + parsedRows.length > limit) {
        toast.error("Plan limit reached", {
          description: `Adding ${parsedRows.length} clients would exceed your ${plan} plan limit of ${limit}.`,
        });
        if (csvInputRef.current) csvInputRef.current.value = "";
        return;
      }

      setUploading(true);
      bulkCreate.mutate(parsedRows);
      if (csvInputRef.current) csvInputRef.current.value = "";
    };
    reader.readAsText(file);
  };

  const handleIntegrationImport = (importData: any[]) => {
    if (currentClientCount + importData.length > limit) {
      toast.error("Plan limit reached", {
        description: `Adding ${importData.length} clients would exceed your ${plan} plan limit of ${limit}.`,
      });
      return;
    }
    const dataWithUser = importData.map((item) => ({
      ...item,
      type: "individual" as const,
      assignedMemberId: currentUserId,
    }));
    setUploading(true);
    bulkCreate.mutate(dataWithUser);
  };

  function validate() {
    if (!canManageClients) {
      toast.error("Access denied", {
        description: "You do not have permission to add clients.",
      });
      return false;
    }
    const e: Partial<NewClientForm> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Enter a valid email";
    const phoneResult = normalizePhoneNumber(
      form.phone,
      form.phoneCountry as any,
    );
    if (!phoneResult.ok) e.phone = phoneResult.error;
    const whatsappResult = normalizePhoneNumber(
      form.whatsappPhone,
      form.whatsappPhoneCountry as any,
    );
    if (!whatsappResult.ok) e.whatsappPhone = whatsappResult.error;
    if (!form.assignedMemberId.trim())
      e.assignedMemberId = "Assigned team member is required" as any;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    setSaving(true);
    createClient.mutate({
      ...form,
      assignedMemberId: form.assignedMemberId,
    });
  }

  function field<K extends keyof NewClientForm>(key: K) {
    return {
      value: form[key] as string,
      onChange: (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => setForm((f) => ({ ...f, [key]: e.target.value })),
    };
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-md sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            Add client
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground flex items-center justify-between">
            <span>Add a client manually or bulk import via CSV.</span>
            {plan !== "pro" && (
              <span
                className={`font-bold ${currentClientCount >= limit ? "text-destructive" : "text-(--brand)"}`}
              >
                {currentClientCount}/{limit} clients used
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-(--colors-stone-50) dark:bg-(--colors-stone-100) border border-(--border) p-3 flex flex-col gap-3 mt-1">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-[11px] font-bold text-foreground">
                Bulk Import & Integrations
              </p>
              <p className="text-[10px] text-muted-foreground">
                Add multiple clients via CSV or import from accounting.
              </p>
            </div>

            <div className="flex flex-row gap-2 w-full">
              <button
                onClick={downloadTemplate}
                className="flex-1 px-2.5 py-1.5 bg-card border border-(--border) text-[10px] font-bold hover:bg-muted transition-colors text-center"
              >
                Use Template
              </button>

              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleCsvUpload}
              />

              <button
                disabled={uploading}
                onClick={() => csvInputRef.current?.click()}
                className="flex-1 px-2.5 py-1.5 bg-foreground text-background text-[10px] font-bold hover:opacity-90 transition-opacity disabled:opacity-50 text-center"
              >
                {uploading ? "Importing..." : "Upload CSV"}
              </button>
            </div>
          </div>

          {(qboConnected || xeroConnected) && (
            <div className="flex gap-2 border-t border-(--border) pt-3">
              {qboConnected && (
                <button
                  onClick={() => setShowQboImport(true)}
                  className="px-2.5 py-1.5 bg-card border border-(--border) text-[10px] font-bold hover:bg-muted transition-colors flex items-center gap-1.5"
                >
                  <img
                    src={QuickBooksLogo}
                    alt="QuickBooks"
                    className="w-3.5 h-3.5 object-contain dark:invert"
                  />
                  Import QBO Customers
                </button>
              )}
              {xeroConnected && (
                <button
                  onClick={() => setShowXeroImport(true)}
                  className="px-2.5 py-1.5 bg-card border border-(--border) text-[10px] font-bold hover:bg-muted transition-colors flex items-center gap-1.5"
                >
                  <img
                    src={XeroLogo}
                    alt="Xero"
                    className="w-3.5 h-3.5 object-contain"
                  />
                  Import Xero Contacts
                </button>
              )}
            </div>
          )}
        </div>

        <FormDivider label="Manual Entry" />

        <div className="space-y-3.5 mt-1">
          {/* Type toggle */}
          <div>
            <Label>Client type</Label>
            <div className="flex border border-(--border) overflow-hidden w-fit">
              {(["individual", "business"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: t }))}
                  className={`px-4 h-8 text-[11px] font-semibold capitalize transition-colors border-r border-(--border) last:border-r-0 ${
                    form.type === t
                      ? "bg-foreground text-background"
                      : "bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="client-name" required>
              {form.type === "business" ? "Business name" : "Full name"}
            </Label>
            <Input
              id="client-name"
              placeholder={
                form.type === "business"
                  ? "Hartwell & Associates"
                  : "Dr. Maria Ruiz"
              }
              className="h-9 text-[12px]"
              {...field("name")}
            />
            <FieldError msg={errors.name} />
          </div>

          <div>
            <Label htmlFor="client-email" required>
              Email
            </Label>
            <Input
              id="client-email"
              type="email"
              placeholder="admin@example.com"
              className="h-9 text-[12px]"
              {...field("email")}
            />
            <FieldError msg={errors.email} />
          </div>

          <div className="border border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
            <button
              type="button"
              onClick={() => setAdvancedOpen((open) => !open)}
              className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-(--surface-hover) transition-colors"
            >
              <div>
                <p className="text-[11px] font-bold text-foreground">
                  Advanced onboarding details
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Phone, WhatsApp, notes, and team assignment.
                </p>
              </div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className={`transition-transform ${advancedOpen ? "rotate-180" : ""}`}
              >
                <path
                  d="M3 5l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </svg>
            </button>

            {advancedOpen && (
              <div className="space-y-3.5 border-t border-(--border) bg-card p-3">
                <PhoneField
                  idPrefix="client"
                  label="Phone (optional)"
                  phoneValue={form.phone}
                  countryValue={form.phoneCountry}
                  onPhoneChange={(value) =>
                    setForm((f) => ({ ...f, phone: value }))
                  }
                  onCountryChange={(value) =>
                    setForm((f) => ({ ...f, phoneCountry: value }))
                  }
                  error={errors.phone}
                  placeholder="20 7946 0042"
                />

                <PhoneField
                  idPrefix="client-whatsapp"
                  label="WhatsApp phone (optional)"
                  phoneValue={form.whatsappPhone}
                  countryValue={form.whatsappPhoneCountry}
                  onPhoneChange={(value) =>
                    setForm((f) => ({ ...f, whatsappPhone: value }))
                  }
                  onCountryChange={(value) =>
                    setForm((f) => ({ ...f, whatsappPhoneCountry: value }))
                  }
                  error={errors.whatsappPhone}
                  placeholder="7911 123456"
                />

                <div>
                  <Label htmlFor="client-notes">Notes (optional)</Label>
                  <textarea
                    id="client-notes"
                    rows={3}
                    placeholder="Any useful context, onboarding notes, or engagement-letter status…"
                    className="w-full border border-(--border) bg-card px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-(--brand) resize-none transition-colors"
                    {...field("notes")}
                  />
                </div>

                <div>
                  <Label htmlFor="client-member">Assigned team member</Label>
                  <TeamMemberSelect
                    value={form.assignedMemberId || ""}
                    onChange={(val) =>
                      setForm((f) => ({ ...f, assignedMemberId: val }))
                    }
                  />
                  <FieldError msg={errors.assignedMemberId} />
                </div>
              </div>
            )}
          </div>
        </div>

        <ModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={saving}
          submitLabel="Add client"
        />

        {!canManageClients && (
          <div className="mt-3 p-2 bg-(--warn-bg) border border-(--warn-border) rounded">
            <p className="text-[10px] text-(--warn) font-bold text-center">
              Only administrators can add or import clients.
            </p>
          </div>
        )}
      </DialogContent>

      <Dialog open={showDownloadConfirm} onOpenChange={setShowDownloadConfirm}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-bold">
              Download CSV Template
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-relaxed">
              This template contains sample/placeholder data to show the correct
              column structure. Please replace the sample rows with your actual
              client details before uploading.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setShowDownloadConfirm(false)}
              className="px-4 py-1.5 border border-(--border) text-[11px] font-bold hover:bg-muted transition-colors rounded-xl"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowDownloadConfirm(false);
                triggerDownloadTemplate();
              }}
              className="px-4 py-1.5 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity rounded-xl"
            >
              Download Template
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showValidationErrors}
        onOpenChange={setShowValidationErrors}
      >
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-bold text-destructive">
              CSV Validation Failed
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              We found some issues with the data in your CSV. Please fix these
              errors in your file and try uploading again.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 py-2 space-y-3 border-y border-border my-2 pr-1">
            {validationErrors.map((err) => (
              <div
                key={err.row}
                className="text-[11px] bg-destructive/5 border border-destructive/20 p-2.5 rounded-lg space-y-1"
              >
                <div className="flex justify-between font-bold text-foreground">
                  <span>{err.name}</span>
                  <span className="text-muted-foreground text-[10px]">
                    Row {err.row}
                  </span>
                </div>
                <ul className="list-disc pl-4 space-y-0.5 text-destructive font-medium">
                  {err.errors.map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-2">
            <button
              onClick={() => setShowValidationErrors(false)}
              className="px-4 py-1.5 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity w-full rounded-xl"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <IntegrationImportModal
        open={showQboImport}
        onClose={() => setShowQboImport(false)}
        provider="qbo"
        onImport={handleIntegrationImport}
      />

      <IntegrationImportModal
        open={showXeroImport}
        onClose={() => setShowXeroImport(false)}
        provider="xero"
        onImport={handleIntegrationImport}
      />
    </Dialog>
  );
}

function IntegrationImportModal({
  open,
  onClose,
  provider,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  provider: "qbo" | "xero";
  onImport: (
    selected: {
      name: string;
      email: string;
      phone?: string;
      qboCustomerId?: string;
      xeroContactId?: string;
    }[],
  ) => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const qboQuery = trpc.integrations.listQboCustomers.useQuery(undefined, {
    enabled: open && provider === "qbo",
  });

  const xeroQuery = trpc.integrations.listXeroContacts.useQuery(undefined, {
    enabled: open && provider === "xero",
  });

  const isLoading =
    provider === "qbo" ? qboQuery.isLoading : xeroQuery.isLoading;

  const rawContacts = useMemo(() => {
    if (provider === "qbo") {
      const customers = (qboQuery.data as any) || [];
      return customers.map((c: any) => ({
        id: c.Id,
        name: c.DisplayName || c.CompanyName || "Unnamed Customer",
        email: c.PrimaryEmailAddr?.Address || "",
        phone: c.PrimaryPhone?.FreeFormNumber || "",
      }));
    } else {
      const contacts = (xeroQuery.data as any) || [];
      return contacts.map((c: any) => {
        const mobilePhone =
          c.Phones?.find(
            (p: any) => p.PhoneType === "MOBILE" || p.PhoneType === "DEFAULT",
          )?.PhoneNumber || "";
        return {
          id: c.ContactID,
          name: c.Name || "Unnamed Contact",
          email: c.EmailAddress || "",
          phone: mobilePhone || "",
        };
      });
    }
  }, [provider, qboQuery.data, xeroQuery.data]);

  const filteredContacts = useMemo(() => {
    const s = search.toLowerCase();
    return rawContacts.filter(
      (c: any) =>
        c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s),
    );
  }, [rawContacts, search]);

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map((c: any) => c.id)));
    }
  };

  const handleImportSubmit = () => {
    const toImport = rawContacts.filter((c: any) => selectedIds.has(c.id));
    const withNoEmail = toImport.filter((c: any) => !c.email.trim());
    if (withNoEmail.length > 0) {
      toast.error(
        "Some selected contacts do not have an email address. Email is required to import.",
        {
          description: `Failed to import: ${withNoEmail
            .slice(0, 3)
            .map((c: any) => c.name)
            .join(", ")}`,
        },
      );
      return;
    }

    onImport(
      toImport.map((c: any) => ({
        name: c.name,
        email: c.email,
        phone: c.phone || undefined,
        ...(provider === "qbo"
          ? { qboCustomerId: c.id }
          : { xeroContactId: c.id }),
      })),
    );
    setSelectedIds(new Set());
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            Import from {provider === "qbo" ? "QuickBooks Online" : "Xero"}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Select the contacts you want to import directly into Quire.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-3 flex-1 flex flex-col min-h-0">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts by name or email…"
            className="h-9 text-[12px]"
          />

          <div className="flex-1 overflow-y-auto border border-border bg-muted/20 min-h-0 rounded-lg p-1 space-y-1">
            {isLoading ? (
              <div className="text-center py-8 text-[11px] text-muted-foreground">
                Loading contacts...
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-8 text-[11px] text-muted-foreground">
                No contacts found.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border/40 text-[10px] font-bold text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size > 0 &&
                      selectedIds.size === filteredContacts.length
                    }
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                  <span>Select All ({filteredContacts.length})</span>
                </div>
                {filteredContacts.map((c: any) => (
                  <div
                    key={c.id}
                    onClick={() => handleToggleSelect(c.id)}
                    className="flex items-center gap-2 px-2.5 py-2 hover:bg-card border border-transparent hover:border-border rounded-md transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => {}}
                      className="rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-bold text-foreground truncate">
                        {c.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {c.email || "No email"} {c.phone ? `• ${c.phone}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 border border-(--border) text-[11px] font-bold hover:bg-muted transition-colors rounded-xl"
          >
            Cancel
          </button>
          <button
            onClick={handleImportSubmit}
            disabled={selectedIds.size === 0}
            className="px-4 py-1.5 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity disabled:opacity-50 rounded-xl"
          >
            Import {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── TeamMemberSelect ────────────────────────────────────────────────────────

function TeamMemberSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: string) => void;
}) {
  const members_q = trpc.workspaces.listMembers.useQuery();
  const members = members_q.data ?? [];

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={members.length === 0}
      className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <option value="">Select member…</option>
      {members.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name} ({m.role})
        </option>
      ))}
    </select>
  );
}

// ─── EditClientModal ──────────────────────────────────────────────────────────

interface EditClientModalProps {
  open: boolean;
  onClose: () => void;
  client: {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
    whatsappPhone?: string | null;
    type: "individual" | "business";
    notes?: string | null;
    assignedMemberId?: string | null;
    status: "active" | "archived";
  } | null;
}

export function EditClientModal({
  open,
  onClose,
  client,
}: EditClientModalProps) {
  const [form, setForm] = useState<NewClientForm>({
    name: client?.name || "",
    email: client?.email || "",
    phone: client?.phone || "",
    phoneCountry: derivePhoneCountry(client?.phone),
    whatsappPhone: client?.whatsappPhone || "",
    whatsappPhoneCountry: derivePhoneCountry(client?.whatsappPhone),
    type: client?.type || "individual",
    notes: client?.notes || "",
    assignedMemberId: client?.assignedMemberId || "",
    status: client?.status || "active",
  });
  const [errors, setErrors] = useState<Partial<NewClientForm>>({});
  const [saving, setSaving] = useState(false);
  const utils = trpc.useUtils();

  const updateClient = trpc.clients.update.useMutation({
    onSuccess() {
      toast.success("Client updated");
      utils.clients.list.invalidate();
      utils.clients.get.invalidate({ id: client?.id || "" });
      onClose();
    },
    onError(err) {
      toast.error(err.message || "Failed to update client");
    },
    onSettled() {
      setSaving(false);
    },
  });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        email: client.email || "",
        phone: client.phone || "",
        phoneCountry: derivePhoneCountry(client.phone),
        whatsappPhone: client.whatsappPhone || "",
        whatsappPhoneCountry: derivePhoneCountry(client.whatsappPhone),
        type: client.type || "individual",
        notes: client.notes || "",
        assignedMemberId: client.assignedMemberId || "",
        status: client.status || "active",
      });
    }
  }, [client]);

  function validate() {
    const e: Partial<NewClientForm> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Enter a valid email";
    const phoneResult = normalizePhoneNumber(
      form.phone,
      form.phoneCountry as any,
    );
    if (!phoneResult.ok) e.phone = phoneResult.error;
    const whatsappResult = normalizePhoneNumber(
      form.whatsappPhone,
      form.whatsappPhoneCountry as any,
    );
    if (!whatsappResult.ok) e.whatsappPhone = whatsappResult.error;
    if (!form.assignedMemberId.trim())
      e.assignedMemberId = "Assigned team member is required" as any;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    setSaving(true);
    updateClient.mutate({
      id: client?.id || "",
      ...form,
      assignedMemberId: form.assignedMemberId,
    });
  }

  function field<K extends keyof NewClientForm>(key: K) {
    return {
      value: form[key] as string,
      onChange: (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => setForm((f) => ({ ...f, [key]: e.target.value })),
    };
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-md sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            Edit client
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5 mt-1">
          <div>
            <Label htmlFor="edit-client-name" required>
              Name
            </Label>
            <Input
              id="edit-client-name"
              className="h-9 text-[12px]"
              {...field("name")}
            />
            <FieldError msg={errors.name} />
          </div>

          <div>
            <Label htmlFor="edit-client-email" required>
              Email
            </Label>
            <Input
              id="edit-client-email"
              type="email"
              className="h-9 text-[12px]"
              {...field("email")}
            />
            <FieldError msg={errors.email} />
          </div>

          <PhoneField
            idPrefix="edit-client"
            label="Phone (optional)"
            phoneValue={form.phone}
            countryValue={form.phoneCountry}
            onPhoneChange={(value) => setForm((f) => ({ ...f, phone: value }))}
            onCountryChange={(value) =>
              setForm((f) => ({ ...f, phoneCountry: value }))
            }
            error={errors.phone}
            placeholder="20 7946 0042 or +44 20 7946 0042"
          />

          {/*<PhoneField
                        idPrefix="edit-client-whatsapp"
                        label="WhatsApp phone (optional)"
                        phoneValue={form.whatsappPhone}
                        countryValue={form.whatsappPhoneCountry}
                        onPhoneChange={( value ) => setForm( f => ( { ...f, whatsappPhone: value } ) )}
                        onCountryChange={( value ) => setForm( f => ( { ...f, whatsappPhoneCountry: value } ) )}
                        error={errors.whatsappPhone}
                        placeholder="7911 123456 or +44 7911 123456"
                    />*/}

          <div>
            <Label htmlFor="edit-client-notes">Notes (optional)</Label>
            <textarea
              id="edit-client-notes"
              rows={3}
              className="w-full border border-(--border) bg-card px-3 py-2 text-[12px] text-foreground focus:outline-none focus:border-(--brand) resize-none transition-colors"
              {...field("notes")}
            />
          </div>

          <div>
            <Label htmlFor="edit-client-status">Status</Label>
            <select
              id="edit-client-status"
              className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors"
              {...field("status")}
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <FormDivider label="Assignment" />

          <div>
            <Label htmlFor="edit-client-member">Assigned team member</Label>
            <TeamMemberSelect
              value={form.assignedMemberId || ""}
              onChange={(val) =>
                setForm((f) => ({ ...f, assignedMemberId: val }))
              }
            />
            <FieldError msg={errors.assignedMemberId} />
          </div>
        </div>

        <ModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={saving}
          submitLabel="Save changes"
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── NewRequestModal ──────────────────────────────────────────────────────────

interface NewRequestItem {
  id: string;
  label: string;
  allowedFileTypes: RequestUploadType[];
}

type RequestUploadType =
  | "pdf"
  | "images"
  | "documents"
  | "spreadsheets"
  | "csv";

const REQUEST_UPLOAD_TYPE_OPTIONS: Array<{
  id: RequestUploadType;
  label: string;
  hint: string;
}> = [
  { id: "pdf", label: "PDF", hint: "Statements, reports, signed exports" },
  { id: "images", label: "Images", hint: "JPG, PNG" },
  { id: "documents", label: "Word docs", hint: "DOC and DOCX files" },
  { id: "spreadsheets", label: "Spreadsheets", hint: "XLS and XLSX workbooks" },
  { id: "csv", label: "CSV", hint: "Flat exports and ledgers" },
];

const DEFAULT_REQUEST_UPLOAD_TYPES: RequestUploadType[] =
  REQUEST_UPLOAD_TYPE_OPTIONS.map((option) => option.id);

interface NewRequestForm {
  clientId: string;
  title: string;
  description: string;
  dueDate: string;
  sendNow: boolean;
  sendSms: boolean;
  sendWhatsapp: boolean;
  items: NewRequestItem[];
  isRecurring: boolean;
  recurringInterval: "days" | "weeks" | "months";
  recurringValue: number;
  dueDateOffsetDays: number;
}

const EMPTY_REQUEST: NewRequestForm = {
  clientId: "",
  title: "",
  description: "",
  dueDate: "",
  sendNow: true,
  sendSms: false,
  sendWhatsapp: false,
  items: [
    { id: "1", label: "", allowedFileTypes: [...DEFAULT_REQUEST_UPLOAD_TYPES] },
  ],
  isRecurring: false,
  recurringInterval: "months",
  recurringValue: 1,
  dueDateOffsetDays: 7,
};

export function NewRequestModal({
  open,
  onClose,
  prefilledClientId,
  clients = [],
  clientPhone,
}: {
  open: boolean;
  onClose: () => void;
  prefilledClientId?: string;
  clients?: { id: string; name: string }[];
  clientPhone?: string;
}) {
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const canUsePaidMessaging = canUsePaidMessagingFromSession(session);
  const hasValidClientPhone = isValidStoredPhoneNumber(clientPhone);

  const [form, setForm] = useState<NewRequestForm>(() => {
    const today = new Date().toISOString().split("T")[0] as string;
    return {
      ...EMPTY_REQUEST,
      clientId: prefilledClientId ?? "",
      dueDate: today,
    };
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof NewRequestForm, string>>
  >({});
  const [saving, setSaving] = useState(false);

  const createRequest = trpc.requests.create.useMutation();
  const sendRequest = trpc.requests.send.useMutation();
  const utils = trpc.useUtils();

  function validate() {
    const e: Partial<Record<keyof NewRequestForm, string>> = {};
    if (!form.clientId) e.clientId = "Select a client";
    if (!form.title.trim()) e.title = "Title is required";

    // Ensure at least one item is provided or clear empty ones?
    // We'll just filter out empty items on submit, but if all are empty and we need documents:
    const validItems = form.items.filter((i) => i.label.trim());
    if (validItems.length === 0)
      e.title = "Add at least one document requirement";

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSaving(true);
    const itemsToSubmit = form.items
      .filter((i) => i.label.trim())
      .map((i) => ({
        label: i.label.trim(),
        allowedFileTypes:
          i.allowedFileTypes.length > 0
            ? i.allowedFileTypes
            : [...DEFAULT_REQUEST_UPLOAD_TYPES],
      }));

    try {
      const req = await createRequest.mutateAsync({
        clientId: form.clientId,
        title: form.title,
        description: form.description,
        dueDate: form.dueDate,
        status: form.sendNow ? "pending" : "draft",
        items: itemsToSubmit,
        isRecurring: form.isRecurring,
        recurringInterval: form.isRecurring ? form.recurringInterval : null,
        recurringValue: form.isRecurring ? form.recurringValue : null,
        dueDateOffsetDays: form.isRecurring ? form.dueDateOffsetDays : null,
      });

      if (form.sendNow) {
        const channels: ("email" | "sms" | "whatsapp")[] = ["email"];
        if (canUsePaidMessaging && form.sendSms) channels.push("sms");
        if (canUsePaidMessaging && form.sendWhatsapp) channels.push("whatsapp");

        await sendRequest.mutateAsync({
          requestId: req.id,
          channels,
        });
      }

      utils.requests.list.invalidate();
      if (prefilledClientId) {
        utils.clients.get.invalidate({ id: prefilledClientId });
      }

      toast.success(form.sendNow ? "Request sent" : "Request saved as draft");
      onClose();
      setForm({
        ...EMPTY_REQUEST,
        dueDate: new Date().toISOString().split("T")[0] as string,
      });
    } catch (err: any) {
      toast.error(err.message || "Failed to save request");
    } finally {
      setSaving(false);
    }
  }

  const TEMPLATES = [
    "Bank statements (12 months)",
    "Tax year receipts",
    "Payroll records",
    "VAT invoices",
    "Management accounts",
    "Annual accounts documents",
    "P60 / P11D forms",
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-md sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            New document request
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            A magic link is emailed to your client. No account needed on their
            end.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 mt-1">
          {/* Client */}
          {!prefilledClientId && (
            <div>
              <Label required>Client</Label>
              <select
                value={form.clientId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, clientId: e.target.value }))
                }
                className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors"
              >
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <FieldError msg={errors.clientId} />
            </div>
          )}

          {/* Title */}
          <div className="relative z-50">
            <Label required>Request title</Label>
            <TemplateInput
              type="request_title"
              placeholder="e.g. Annual Accounts 2024/25"
              value={form.title}
              onChange={(val) => setForm((f) => ({ ...f, title: val }))}
            />
            <FieldError msg={errors.title} />
          </div>

          <FormDivider label="Documents requested" />

          <div className="space-y-2">
            {form.items.map((item, index) => (
              <div
                key={item.id}
                className="border border-(--border) bg-card p-3 space-y-2"
              >
                <div className="flex gap-2">
                  <div className="flex-1">
                    <TemplateInput
                      type="document_name"
                      placeholder={`Document ${index + 1} (e.g. Bank statements)`}
                      value={item.label}
                      onChange={(val) => {
                        setForm((f) => ({
                          ...f,
                          items: f.items.map((it, i) =>
                            i === index ? { ...it, label: val } : it,
                          ),
                        }));
                      }}
                    />
                  </div>
                  {form.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = form.items.filter(
                          (_, i) => i !== index,
                        );
                        setForm((f) => ({ ...f, items: newItems }));
                      }}
                      className="h-9 w-9 flex items-center justify-center border border-(--border) text-muted-foreground hover:bg-(--colors-red-50) hover:text-(--warn) hover:border-(--colors-red-200) transition-colors shrink-0"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 2l8 8M10 2L2 10"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="square"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-2">
                    Allowed upload types
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {REQUEST_UPLOAD_TYPE_OPTIONS.map((option) => {
                      const checked = item.allowedFileTypes.includes(option.id);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              items: f.items.map((it, i) => {
                                if (i !== index) return it;
                                const nextTypes = checked
                                  ? it.allowedFileTypes.filter(
                                      (type) => type !== option.id,
                                    )
                                  : [...it.allowedFileTypes, option.id];
                                return {
                                  ...it,
                                  allowedFileTypes:
                                    nextTypes.length > 0
                                      ? nextTypes
                                      : [...DEFAULT_REQUEST_UPLOAD_TYPES],
                                };
                              }),
                            }));
                          }}
                          className={`px-2.5 py-1.5 border text-[10px] font-semibold transition-colors ${
                            checked
                              ? "border-(--brand) bg-(--brand-dim) text-(--brand)"
                              : "border-(--border) text-muted-foreground hover:bg-(--surface-hover)"
                          }`}
                          title={option.hint}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setForm((f) => ({
                  ...f,
                  items: [
                    ...f.items,
                    {
                      id: crypto.randomUUID(),
                      label: "",
                      allowedFileTypes: [...DEFAULT_REQUEST_UPLOAD_TYPES],
                    },
                  ],
                }));
              }}
              className="text-[11px] font-semibold text-(--brand) hover:underline"
            >
              + Add another document
            </button>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="req-desc">Instructions for client (optional)</Label>
            <textarea
              id="req-desc"
              rows={2}
              placeholder="e.g. Please include all 12 months from your Barclays account in PDF format."
              className="w-full border border-(--border) bg-card px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-(--brand) resize-none transition-colors"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>

          {/* Due date */}
          <div>
            <Label htmlFor="req-due">Due date (optional)</Label>
            <input
              id="req-due"
              type="date"
              className="w-full max-w-full min-w-0 h-9 border border-(--border) bg-card px-3 text-[16px] text-foreground focus:outline-none focus:border-(--brand) transition-colors appearance-none box-border mt-1"
              value={form.dueDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, dueDate: e.target.value }))
              }
            />
          </div>

          {/* Send now toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
            <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.sendNow}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sendNow: e.target.checked }))
                }
                className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none"
              />
              <svg
                className="pointer-events-none relative z-10 hidden peer-checked:block"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M1.5 5L3.5 7L8.5 2"
                  stroke="var(--background)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-foreground">
                Send magic link now
              </p>
              <p className="text-[10px] text-muted-foreground">
                Client receives an email with a direct upload link
              </p>
            </div>
          </label>

          {/* SMS toggle — only when client has a phone */}
          {/* SMS toggle — only when client has a phone */}
          {hasValidClientPhone && (
            <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
              <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={form.sendSms && canUsePaidMessaging}
                  disabled={!canUsePaidMessaging}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sendSms: canUsePaidMessaging && e.target.checked,
                    }))
                  }
                  className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />

                {/* perfectly centered checkmark */}
                <svg
                  className="pointer-events-none relative z-10 hidden peer-checked:block"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1.5 5L3.5 7L8.5 2"
                    stroke="var(--background)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div>
                <p className="text-[12px] font-semibold text-foreground">
                  Also send SMS notification
                </p>

                <p className="text-[10px] text-muted-foreground">
                  {canUsePaidMessaging
                    ? `Client receives a text message to ${clientPhone}`
                    : PAID_MESSAGING_UPGRADE_COPY}
                </p>
              </div>
            </label>
          )}

          {/* Recurring Request Toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
            <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.isRecurring}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isRecurring: e.target.checked }))
                }
                className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none"
              />
              <svg
                className="pointer-events-none relative z-10 hidden peer-checked:block"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M1.5 5L3.5 7L8.5 2"
                  stroke="var(--background)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-foreground">
                Recurring request
              </p>
              <p className="text-[10px] text-muted-foreground">
                Automatically request these documents on a schedule
              </p>
            </div>
          </label>

          {form.isRecurring && (
            <div className="border border-(--border) bg-card p-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label required>Interval</Label>
                  <select
                    value={form.recurringInterval}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        recurringInterval: e.target.value as any,
                      }))
                    }
                    className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                <div>
                  <Label required>Every</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.recurringValue}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        recurringValue: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="h-9 text-[12px]"
                  />
                </div>
              </div>
              <div>
                <Label required>Due date offset (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.dueDateOffsetDays}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      dueDateOffsetDays: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="h-9 text-[12px]"
                  placeholder="e.g. 7 days"
                />
                <p className="text-[9px] text-muted-foreground mt-1">
                  Number of days client has to complete the request after it is
                  automatically sent.
                </p>
              </div>
            </div>
          )}

          {/* WhatsApp toggle — gated on plan, only when client has a phone */}
        </div>

        <ModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={saving}
          submitLabel={form.sendNow ? "Send request" : "Save draft"}
        />
      </DialogContent>
    </Dialog>
  );
}

interface EditRequestModalProps {
  open: boolean;
  onClose: () => void;
  request: {
    id: string;
    title: string;
    description?: string | null;
    dueDate?: string | null;
    status: "draft" | "pending" | "partial" | "received" | "approved";

    items: {
      id: string;
      label: string;
      allowedFileTypes: RequestUploadType[];
    }[];

    isRecurring?: boolean;
    recurringInterval?: string | null;
    recurringValue?: number | null;
    dueDateOffsetDays?: number | null;
  } | null;
}

type EditRequestItem = {
  id?: string;
  uiKey: string;
  label: string;
  allowedFileTypes: RequestUploadType[];
};

type EditRequestForm = {
  title: string;
  description: string;
  dueDate: string;
  status: "draft" | "pending" | "partial" | "received" | "approved";

  items: EditRequestItem[];

  isRecurring: boolean;
  recurringInterval: "days" | "weeks" | "months";
  recurringValue: number;
  dueDateOffsetDays: number;
};

type EditRequestErrors = Partial<Record<"title", string>>;

function buildFormState(
  request: EditRequestModalProps["request"],
): EditRequestForm {
  const items =
    request?.items && request.items.length > 0
      ? request.items.map((i) => ({
          id: i.id,
          uiKey: i.id,
          label: i.label,
          allowedFileTypes: i.allowedFileTypes,
        }))
      : [
          {
            uiKey: crypto.randomUUID(),
            label: "",
            allowedFileTypes: [...DEFAULT_REQUEST_UPLOAD_TYPES],
          },
        ];

  return {
    title: request?.title ?? "",
    description: request?.description ?? "",
    dueDate: request?.dueDate ?? "",
    status: request?.status ?? "draft",

    items,

    isRecurring: request?.isRecurring ?? false,

    recurringInterval:
      (request?.recurringInterval as "days" | "weeks" | "months" | undefined) ??
      "months",

    recurringValue: request?.recurringValue ?? 1,

    dueDateOffsetDays: request?.dueDateOffsetDays ?? 7,
  };
}

export function EditRequestModal({
  open,
  onClose,
  request,
}: EditRequestModalProps) {
  const [form, setForm] = useState<EditRequestForm>(buildFormState(request));

  useEffect(() => {
    setForm(buildFormState(request));
  }, [request]);

  const [errors, setErrors] = useState<EditRequestErrors>({});
  const [saving, setSaving] = useState(false);
  const utils = trpc.useUtils();

  const updateRequest = trpc.requests.update.useMutation({
    onSuccess() {
      toast.success("Request updated");
      utils.requests.list.invalidate();
      utils.requests.get.invalidate({ id: request?.id || "" });
      onClose();
    },
    onError(err) {
      toast.error(err.message || "Failed to update request");
    },
    onSettled() {
      setSaving(false);
    },
  });

  function validate() {
    const e: Partial<Record<"title", string>> = {};
    if (!form.title.trim()) {
      e.title = "Title is required";
    } else {
      const validItems = form.items.filter((i) => i.label.trim());
      if (validItems.length === 0) {
        e.title = "Add at least one document requirement";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    setSaving(true);
    updateRequest.mutate({
      id: request?.id || "",
      title: form.title,
      description: form.description || null,
      dueDate: form.dueDate || null,
      status: form.status,
      items: form.items
        .filter((i) => i.label.trim())
        .map((i) => ({
          id: i.id,
          label: i.label.trim(),
          allowedFileTypes:
            i.allowedFileTypes.length > 0
              ? i.allowedFileTypes
              : [...DEFAULT_REQUEST_UPLOAD_TYPES],
        })),
      isRecurring: form.isRecurring,
      recurringInterval: form.isRecurring
        ? (form.recurringInterval as "days" | "weeks" | "months")
        : null,
      recurringValue: form.isRecurring ? form.recurringValue : null,
      dueDateOffsetDays: form.isRecurring ? form.dueDateOffsetDays : null,
    });
  }

  function addItem() {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          uiKey: crypto.randomUUID(),
          label: "",
          allowedFileTypes: [...DEFAULT_REQUEST_UPLOAD_TYPES],
        },
      ],
    }));
  }

  function removeItem(index: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== index),
    }));
  }

  function updateItemLabel(index: number, val: string) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === index ? { ...it, label: val } : it)),
    }));
  }

  function toggleItemFileType(index: number, type: RequestUploadType) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => {
        if (i !== index) return it;
        const checked = it.allowedFileTypes.includes(type);
        const nextTypes = checked
          ? it.allowedFileTypes.filter((t) => t !== type)
          : [...it.allowedFileTypes, type];
        return {
          ...it,
          allowedFileTypes:
            nextTypes.length > 0
              ? nextTypes
              : [...DEFAULT_REQUEST_UPLOAD_TYPES],
        };
      }),
    }));
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-md sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            Edit request
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5 mt-1">
          <div className="relative z-50">
            <Label required>Title</Label>
            <TemplateInput
              type="request_title"
              value={form.title}
              onChange={(val) => setForm((f) => ({ ...f, title: val }))}
            />
            <FieldError msg={errors.title} />
          </div>

          <FormDivider label="Documents requested" />

          <div className="space-y-2">
            {form.items.map((item, index) => (
              <div
                key={item.uiKey}
                className="border border-(--border) bg-card p-3 space-y-2"
              >
                <div className="flex gap-2">
                  <div className="flex-1">
                    <TemplateInput
                      type="document_name"
                      placeholder={`Document ${index + 1} (e.g. Bank statements)`}
                      value={item.label}
                      onChange={(val) => updateItemLabel(index, val)}
                    />
                  </div>
                  {form.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="h-9 w-9 flex items-center justify-center border border-(--border) text-muted-foreground hover:bg-(--colors-red-50) hover:text-(--warn) hover:border-(--colors-red-200) transition-colors shrink-0"
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 2l8 8M10 2L2 10"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="square"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-2">
                    Allowed upload types
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {REQUEST_UPLOAD_TYPE_OPTIONS.map((option) => {
                      const checked = item.allowedFileTypes.includes(option.id);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => toggleItemFileType(index, option.id)}
                          className={`px-2.5 py-1.5 border text-[10px] font-semibold transition-colors ${
                            checked
                              ? "border-(--brand) bg-(--brand-dim) text-(--brand)"
                              : "border-(--border) text-muted-foreground hover:bg-(--surface-hover)"
                          }`}
                          title={option.hint}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addItem}
              className="text-[11px] font-semibold text-(--brand) hover:underline"
            >
              + Add another document
            </button>
          </div>

          <div>
            <Label>Instructions (optional)</Label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              className="w-full border border-(--border) bg-card px-3 py-2 text-[12px] text-foreground focus:outline-none focus:border-(--brand) resize-none"
            />
          </div>

          <div>
            <Label>Due date</Label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, dueDate: e.target.value }))
              }
              className="w-full max-w-full min-w-0 h-9 border border-(--border) bg-card px-3 text-[16px] text-foreground focus:outline-none focus:border-(--brand) transition-colors appearance-none box-border mt-1"
            />
          </div>

          <div>
            <Label>Status</Label>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as any }))
              }
              className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground"
            >
              {["draft", "pending", "partial", "received", "approved"].map(
                (s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ),
              )}
            </select>
          </div>

          {/* Recurring Request Toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
            <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.isRecurring}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isRecurring: e.target.checked }))
                }
                className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none"
              />
              <svg
                className="pointer-events-none relative z-10 hidden peer-checked:block"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M1.5 5L3.5 7L8.5 2"
                  stroke="var(--background)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-foreground">
                Recurring request
              </p>
              <p className="text-[10px] text-muted-foreground">
                Automatically request these documents on a schedule
              </p>
            </div>
          </label>

          {form.isRecurring && (
            <div className="border border-(--border) bg-card p-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label required>Interval</Label>
                  <select
                    value={form.recurringInterval}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        recurringInterval: e.target.value as any,
                      }))
                    }
                    className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors"
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                <div>
                  <Label required>Every</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.recurringValue}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        recurringValue: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="h-9 text-[12px]"
                  />
                </div>
              </div>
              <div>
                <Label required>Due date offset (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.dueDateOffsetDays}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      dueDateOffsetDays: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="h-9 text-[12px]"
                  placeholder="e.g. 7 days"
                />
                <p className="text-[9px] text-muted-foreground mt-1">
                  Number of days client has to complete the request after it is
                  automatically sent.
                </p>
              </div>
            </div>
          )}
        </div>

        <ModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={saving}
          submitLabel="Save changes"
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── NewSignatureModal ────────────────────────────────────────────────────────

interface NewSigForm {
  clientId: string;
  documentId: string;
  documentName: string;
  description: string;
  dueDate: string;
  sendNow: boolean;
  sendSms: boolean;
  sendWhatsapp: boolean;
}

const EMPTY_SIG: NewSigForm = {
  clientId: "",
  documentId: "",
  documentName: "",
  description: "",
  dueDate: "",
  sendNow: true,
  sendSms: false,
  sendWhatsapp: false,
};

export function NewSignatureModal({
  open,
  onClose,
  prefilledClientId,
  clients = [],
  clientPhone,
}: {
  open: boolean;
  onClose: () => void;
  prefilledClientId?: string;
  clients?: { id: string; name: string }[];
  clientPhone?: string;
}) {
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const canUsePaidMessaging = canUsePaidMessagingFromSession(session);
  const hasValidClientPhone = isValidStoredPhoneNumber(clientPhone);

  const [form, setForm] = useState<NewSigForm>(() => {
    const today = new Date().toISOString().split("T")[0] as string;
    return {
      ...EMPTY_SIG,
      clientId: prefilledClientId ?? "",
      dueDate: today,
    };
  });
  const [sigMode, setSigMode] = useState<"text" | "upload" | "existing">(
    "text",
  );
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedClientId, setSelectedClientId] = useState(
    prefilledClientId ?? "",
  );
  const [docSearchQuery, setDocSearchQuery] = useState("");
  const [errors, setErrors] = useState<
    Partial<Record<keyof NewSigForm, string>>
  >({});
  const [saving, setSaving] = useState(false);
  const utils = trpc.useUtils();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const initialClientId = prefilledClientId ?? "";
      const today = new Date().toISOString().split("T")[0] as string;
      setSelectedClientId(initialClientId);
      setForm({
        ...EMPTY_SIG,
        clientId: initialClientId,
        dueDate: today,
        sendNow: true,
      });
      setDocSearchQuery("");
      setUploadFile(null);
      setSigMode("text");
      setErrors({});
    }
  }, [open, prefilledClientId]);

  useEffect(() => {
    if (sigMode === "text") {
      setForm((f) => ({ ...f, sendNow: true }));
    } else if (sigMode === "upload" && uploadFile) {
      const isPdf =
        uploadFile.type === "application/pdf" ||
        uploadFile.name.toLowerCase().endsWith(".pdf");
      setForm((f) => ({ ...f, sendNow: !isPdf }));
    } else {
      setForm((f) => ({ ...f, sendNow: false }));
    }
  }, [sigMode, uploadFile]);

  const docs_q = trpc.documents.list.useQuery(
    { clientId: selectedClientId },
    { enabled: !!selectedClientId },
  );
  const docs = docs_q.data ?? [];
  const pdfDocs = docs.filter(
    (d) =>
      d.mimeType === "application/pdf" ||
      d.fileName.toLowerCase().endsWith(".pdf"),
  );

  const filteredDocs = pdfDocs.filter((d) =>
    d.fileName.toLowerCase().includes(docSearchQuery.toLowerCase()),
  );

  const createSig = trpc.signatures.create.useMutation();
  const sendSig = trpc.signatures.send.useMutation();

  function validate() {
    const e: Partial<Record<keyof NewSigForm, string>> = {};
    if (!form.clientId) e.clientId = "Select a client";

    if (sigMode === "text") {
      if (!form.documentName.trim())
        e.documentName = "Document name is required";
    } else if (sigMode === "upload") {
      if (!uploadFile) {
        e.documentId = "Select a file to upload and sign";
      }
    } else {
      if (!form.documentId) e.documentId = "Select a file to sign";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSaving(true);

    try {
      let signatureId: string | undefined;

      const deliveryChannels: ("email" | "sms" | "whatsapp")[] = ["email"];
      if (canUsePaidMessaging && form.sendSms) deliveryChannels.push("sms");
      if (canUsePaidMessaging && form.sendWhatsapp)
        deliveryChannels.push("whatsapp");

      if (sigMode === "text") {
        const res = await createSig.mutateAsync({
          clientId: form.clientId,
          documentName: form.documentName.trim(),
          description: form.description,
          dueDate: form.dueDate || undefined,
          deliveryChannels,
        });
        signatureId = res.id;
      } else if (sigMode === "upload" && uploadFile) {
        // 1. Get Intent
        const intentRes = await fetch(`${runtimeConfig.apiUrl}/upload/intent`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: uploadFile.name,
            fileSize: uploadFile.size,
            mimeType: uploadFile.type,
            clientId: form.clientId,
            uploadedBy: "member",
          }),
        });
        if (!intentRes.ok) {
          const err = (await intentRes.json().catch(() => ({}))) as any;
          throw new Error(err.error || "Failed to create upload intent");
        }
        const intent = (await intentRes.json()) as {
          uploadId: string;
          token: string;
          uploadUrl: string;
        };

        // 2. Put content directly to R2
        const uploadRes = await fetch(intent.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Length": String(uploadFile.size),
            "Content-Type": uploadFile.type,
          },
          body: uploadFile,
        });
        if (!uploadRes.ok) {
          throw new Error("Failed to upload file content to storage");
        }

        // 3. Finalize upload
        const finalizeRes = await fetch(
          `${runtimeConfig.apiUrl}/upload/finalize`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              uploadId: intent.uploadId,
              token: intent.token,
              encryptionMode: "managed_secure",
            }),
          },
        );
        if (!finalizeRes.ok) {
          throw new Error("Failed to finalize upload record");
        }
        const fin = (await finalizeRes.json()) as { document: { id: string } };

        // 4. Dispatch signature mutation with newly created documentId
        const res = await createSig.mutateAsync({
          clientId: form.clientId,
          documentId: fin.document.id,
          description: form.description,
          dueDate: form.dueDate || undefined,
          deliveryChannels,
        });
        signatureId = res.id;
      } else {
        const res = await createSig.mutateAsync({
          clientId: form.clientId,
          documentId: form.documentId,
          description: form.description,
          dueDate: form.dueDate || undefined,
          deliveryChannels,
        });
        signatureId = res.id;
      }

      if (signatureId && form.sendNow) {
        const channels: ("email" | "sms" | "whatsapp")[] = ["email"];
        if (canUsePaidMessaging && form.sendSms) channels.push("sms");
        if (canUsePaidMessaging && form.sendWhatsapp) channels.push("whatsapp");

        await sendSig.mutateAsync({
          signatureId,
          channels,
        });
      }

      toast.success(
        form.sendNow ? "Signature request sent" : "Signature request saved",
      );
      utils.signatures.list.invalidate();
      onClose();

      const isPdf =
        sigMode === "existing" ||
        (sigMode === "upload" &&
          uploadFile &&
          (uploadFile.type === "application/pdf" ||
            uploadFile.name.toLowerCase().endsWith(".pdf")));

      if (signatureId && !form.sendNow && isPdf) {
        navigate({
          to: "/signatures/$",
          params: { _splat: signatureId },
          search: { prepare: "true" } as any,
        });
      }

      setForm({
        ...EMPTY_SIG,
        dueDate: new Date().toISOString().split("T")[0] as string,
      });
      setDocSearchQuery("");
      setUploadFile(null);
      setSigMode("text");
    } catch (err: any) {
      toast.error(err.message || "Failed to process signature request");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          onClose();
          setForm({
            ...EMPTY_SIG,
            dueDate: new Date().toISOString().split("T")[0] as string,
          });
          setDocSearchQuery("");
          setUploadFile(null);
          setSigMode("text");
        }
      }}
    >
      <DialogContent className="max-w-md sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-bold">
            Request signature
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Client signs directly from their portal link — no account needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 mt-1">
          {!prefilledClientId && (
            <div>
              <Label required>Client</Label>
              <select
                value={form.clientId}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((f) => ({ ...f, clientId: val }));
                  setSelectedClientId(val);
                }}
                className="w-full h-9 border border-(--border) bg-card px-3 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors "
              >
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <FieldError msg={errors.clientId} />
            </div>
          )}

          {/* Mode selection tabs */}
          <div>
            <Label>Signature request type</Label>
            <div className="flex border border-(--border) overflow-hidden w-full mt-1">
              <button
                type="button"
                onClick={() => setSigMode("text")}
                className={`flex-1 text-center py-2 text-[10.5px] font-semibold transition-colors border-r border-(--border) ${
                  sigMode === "text"
                    ? "bg-foreground text-background"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                Plain Agreement
              </button>
              <button
                type="button"
                onClick={() => setSigMode("upload")}
                className={`flex-1 text-center py-2 text-[10.5px] font-semibold transition-colors border-r border-(--border) ${
                  sigMode === "upload"
                    ? "bg-foreground text-background"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                Upload Document
              </button>
              <button
                type="button"
                onClick={() => setSigMode("existing")}
                className={`flex-1 text-center py-2 text-[10.5px] font-semibold transition-colors ${
                  sigMode === "existing"
                    ? "bg-foreground text-background"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                Select Client File
              </button>
            </div>
          </div>

          {/* Mode 1: Plain-Text Agreement */}
          {sigMode === "text" && (
            <div className="space-y-3.5">
              <div>
                <Label required>Agreement Title / Name</Label>
                <Input
                  placeholder="e.g. Q1 VAT Submission Consent"
                  value={form.documentName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, documentName: e.target.value }))
                  }
                  className="h-9 text-[12px] rounded-none"
                />
                <FieldError msg={errors.documentName} />
              </div>
            </div>
          )}

          {/* Mode 2: Upload Document */}
          {sigMode === "upload" && (
            <div className="space-y-3.5">
              <div>
                <Label required>Upload document to sign</Label>
                {form.clientId ? (
                  <div className="mt-1 border border-dashed border-(--border) p-4 text-center bg-card hover:bg-(--surface-hover) transition-colors relative cursor-pointer group">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setUploadFile(file);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <div className="flex h-8 w-8 items-center justify-center bg-(--brand-dim) text-(--brand) border border-(--brand-border)">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <rect
                            x="2"
                            y="1"
                            width="10"
                            height="12"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M7 4v6M4 7h6"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="square"
                          />
                        </svg>
                      </div>
                      <p className="text-[11px] font-bold text-foreground truncate max-w-full px-2">
                        {uploadFile ? uploadFile.name : "Select document"}
                      </p>
                      <p className="text-[9.5px] text-muted-foreground">
                        {uploadFile
                          ? `${(uploadFile.size / 1024).toFixed(1)} KB`
                          : "Drag and drop or browse files (Max 25MB)"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-(--warn) italic mt-1">
                    Please select a client first to upload files for them.
                  </p>
                )}
                <FieldError msg={errors.documentId} />
              </div>
            </div>
          )}

          {/* Mode 2: Select Existing Client File */}
          {sigMode === "existing" && (
            <div className="space-y-3.5">
              <div>
                <Label required>
                  <span className="flex items-center justify-between w-full">
                    <span>Select document to sign</span>
                    {pdfDocs.length > 0 && (
                      <span className="text-[10px] text-muted-foreground font-normal">
                        {filteredDocs.length === pdfDocs.length
                          ? `${pdfDocs.length} files`
                          : `${filteredDocs.length} of ${pdfDocs.length} found`}
                      </span>
                    )}
                  </span>
                </Label>
                {selectedClientId ? (
                  <div className="space-y-2 mt-1">
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search client documents..."
                        value={docSearchQuery}
                        onChange={(e) => setDocSearchQuery(e.target.value)}
                        className="w-full h-9 border border-(--border) bg-card pl-8 pr-3 text-[11px] text-foreground focus:outline-none focus:border-(--brand) transition-colors rounded-none"
                      />
                      <svg
                        className="absolute left-2.5 top-2.5 text-muted-foreground/50"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <circle
                          cx="5"
                          cy="5"
                          r="3.5"
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                        <path
                          d="M7.5 7.5l3.5 3.5"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="square"
                        />
                      </svg>
                    </div>

                    {/* File List Grid */}
                    <div className="border border-(--border) max-h-[140px] overflow-y-auto divide-y divide-(--border) bg-card mt-1.5">
                      {docs_q.isLoading ? (
                        <p className="text-[10px] text-muted-foreground p-3 text-center">
                          Loading client documents...
                        </p>
                      ) : filteredDocs.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground p-3 text-center">
                          {docSearchQuery
                            ? "No matching files found."
                            : "No PDF documents found for this client."}
                        </p>
                      ) : (
                        filteredDocs.map((doc) => {
                          const isSelected = form.documentId === doc.id;
                          return (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() =>
                                setForm((f) => ({ ...f, documentId: doc.id }))
                              }
                              className={`w-full text-left px-3 py-2 flex items-center justify-between text-[11px] transition-colors ${
                                isSelected
                                  ? "bg-(--brand-dim) text-(--brand) font-semibold"
                                  : "hover:bg-(--surface-hover) text-foreground"
                              }`}
                            >
                              <div className="truncate pr-4">
                                <p className="truncate font-medium">
                                  {doc.fileName}
                                </p>
                                <p className="text-[9px] text-muted-foreground/80 mt-0.5 leading-none">
                                  {(doc.fileSize / 1024).toFixed(1)} KB ·{" "}
                                  {new Date(doc.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              {isSelected && (
                                <svg
                                  className="shrink-0 text-(--brand)"
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M2.5 6L4.5 8L9.5 3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                    <FieldError msg={errors.documentId} />
                  </div>
                ) : (
                  <p className="text-[11px] text-(--warn) italic mt-1">
                    Please select a client first to see their files.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* What happens next info */}
          <div className="bg-(--colors-stone-50) dark:bg-(--colors-stone-100) border border-(--border) px-3 py-2.5 space-y-1.5 mt-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
              What happens next
            </p>
            {[
              "Client receives a secure digital signing link",
              "They sign directly in their browser — no app needed",
              "You get notified instantly. IP and timestamp recorded.",
              "Access legally-binding audit records inside your dashboard",
            ].map((step) => (
              <div key={step} className="flex items-start gap-2">
                <svg
                  className="shrink-0 mt-0.5"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M2 5l2 2 4-4"
                    stroke="var(--green)"
                    strokeWidth="1.3"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  />
                </svg>
                <p className="text-[10px] text-muted-foreground">{step}</p>
              </div>
            ))}
          </div>

          <div>
            <Label>Instructions / Agreement details</Label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder={
                sigMode === "text"
                  ? "Add legal terms or agreement statement that client will sign off on…"
                  : "Add any instructions or context for this document sign-off…"
              }
              className="w-full min-h-[80px] border border-(--border) bg-card px-3 py-2 text-[12px] text-foreground focus:outline-none focus:border-(--brand) transition-colors resize-none mt-1"
            />
          </div>

          {/* Due date */}
          <div>
            <Label>
              Signing deadline{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, dueDate: e.target.value }))
              }
              className="w-full max-w-full min-w-0 h-9 border border-(--border) bg-card px-3 text-[16px] text-foreground focus:outline-none focus:border-(--brand) transition-colors appearance-none box-border mt-1"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Sets a deadline. Client gets automated reminders as the date
              approaches.
            </p>
          </div>

          {/* Send now */}
          {(sigMode === "text" ||
            (sigMode === "upload" &&
              uploadFile &&
              !(
                uploadFile.type === "application/pdf" ||
                uploadFile.name.toLowerCase().endsWith(".pdf")
              ))) && (
            <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
              <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={form.sendNow}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sendNow: e.target.checked }))
                  }
                  className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none"
                />
                <svg
                  className="pointer-events-none relative z-10 hidden peer-checked:block"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1.5 5L3.5 7L8.5 2"
                    stroke="var(--background)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[12px] font-semibold text-foreground">
                  Send to client now
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Client receives a signature request email
                </p>
              </div>
            </label>
          )}

          {/* SMS toggle — only when client has a phone */}
          {hasValidClientPhone && (
            <label className="flex items-center gap-3 cursor-pointer select-none border border-(--border) px-3 py-2.5 hover:bg-(--surface-hover) transition-colors">
              <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={form.sendSms && canUsePaidMessaging}
                  disabled={!canUsePaidMessaging}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sendSms: canUsePaidMessaging && e.target.checked,
                    }))
                  }
                  className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
                <svg
                  className="pointer-events-none relative z-10 hidden peer-checked:block"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1.5 5L3.5 7L8.5 2"
                    stroke="var(--background)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[12px] font-semibold text-foreground">
                  Also send SMS notification
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {canUsePaidMessaging
                    ? `Client receives a text message to ${clientPhone}`
                    : PAID_MESSAGING_UPGRADE_COPY}
                </p>
              </div>
            </label>
          )}

          {/* WhatsApp toggle — gated on plan, only when client has a phone */}
        </div>

        <ModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={saving}
          submitLabel={
            form.sendNow
              ? "Send for signature"
              : sigMode !== "text"
                ? "Next: Prepare fields"
                : "Save"
          }
        />
      </DialogContent>
    </Dialog>
  );
}

// {clientPhone && (
//     // <label className={`flex items-center gap-3 select-none border border-(--border) px-3 py-2.5 transition-colors ${canUseWhatsapp ? 'cursor-pointer hover:bg-(--surface-hover)' : 'cursor-not-allowed opacity-70 bg-(--surface-hover)'}`}>
//     //     <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
//     //         <input
//     //             type="checkbox"
//     //             checked={form.sendWhatsapp}
//     //             disabled={!canUseWhatsapp}
//     //             onChange={e => setForm( f => ( { ...f, sendWhatsapp: e.target.checked } ) )}
//     //             className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none disabled:cursor-not-allowed disabled:bg-(--surface-hover) disabled:border-(--border)"
//     //         />

//             {/* perfectly centered checkmark */}
//             {/*<svg
//                 className={`pointer-events-none relative z-10 hidden peer-checked:block ${!canUseWhatsapp ? 'opacity-50' : ''}`}
//                 width="10" height="10" viewBox="0 0 10 10" fill="none"
//             >
//                 <path
//                     d="M1.5 5L3.5 7L8.5 2"
//                     stroke="var(--background)"
//                     strokeWidth="1.5"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                 />
//             </svg>*/}
//         // </div>

//         <div className="flex-1 min-w-0">
//         //     <div className="flex items-center gap-2">
//         //         <p className="text-[12px] font-semibold text-foreground">
//         //             Also send WhatsApp notification
//         //         </p>
//         //         {!canUseWhatsapp && (
//         //             <span className="shrink-0 inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-(--brand-dim) text-(--brand) border border-(--brand-dim) rounded-sm">
//         //                 Pro / Trial
//         //             </span>
//         //         )}
//         //     </div>

//         //     <p className="text-[10px] text-muted-foreground truncate">
//         //         Client receives a WhatsApp message to {clientPhone}
//         //     </p>
//         // </div>
//     // </label>
// )}

// {clientPhone && (
//     <label className={`flex items-center gap-3 select-none border border-(--border) px-3 py-2.5 transition-colors ${canUseWhatsapp ? 'cursor-pointer hover:bg-(--surface-hover)' : 'cursor-not-allowed opacity-70 bg-(--surface-hover)'}`}>
//         <div className="shrink-0 w-4 h-4 relative flex items-center justify-center">
//             <input
//                 type="checkbox"
//                 checked={form.sendWhatsapp}
//                 disabled={!canUseWhatsapp}
//                 onChange={e => setForm( f => ( { ...f, sendWhatsapp: e.target.checked } ) )}
//                 className="peer appearance-none absolute inset-0 border border-(--border) bg-card rounded-none transition-colors checked:bg-foreground checked:border-foreground focus:outline-none disabled:cursor-not-allowed disabled:bg-(--surface-hover) disabled:border-(--border)"
//             />

//             {/* perfectly centered checkmark */}
//             <svg
//                 className={`pointer-events-none relative z-10 hidden peer-checked:block ${!canUseWhatsapp ? 'opacity-50' : ''}`}
//                 width="10" height="10" viewBox="0 0 10 10" fill="none"
//             >
//                 <path
//                     d="M1.5 5L3.5 7L8.5 2"
//                     stroke="var(--background)"
//                     strokeWidth="1.5"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                 />
//             </svg>
//         </div>

//         <div className="flex-1 min-w-0">
//             <div className="flex items-center gap-2">
//                 <p className="text-[12px] font-semibold text-foreground">
//                     Also send WhatsApp notification
//                 </p>
//                 {!canUseWhatsapp && (
//                     <span className="shrink-0 inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-(--brand-dim) text-(--brand) border border-(--brand-dim) rounded-sm">
//                         Pro / Trial
//                     </span>
//                 )}
//             </div>

//             <p className="text-[10px] text-muted-foreground truncate">
//                 Client receives a WhatsApp message to {clientPhone}
//             </p>
//         </div>
//     </label>
// )}
